/**
 * My time: a person's hours for one pay period, and the place to log
 * internal time.
 *
 * One read (GET /v1/my-hours) gives the period, every line the ledger
 * holds for the caller (case-note hours with the client and band,
 * internal time with its kind) and the server's summary of them. The
 * header's Log internal time button opens the internal-time form in a
 * dialog (LogInternalTimeDialog, also My Clients' door); the period
 * header pages by one period from the URL's own date and jumps to a
 * typed date; the summary card holds the three
 * totals and the by-client and internal breakdowns, with the period's
 * internal entries under Internal; the case-note lines list newest
 * first, twenty at a time, each linking to its note on the client's
 * record. Hours show to one decimal, and only hours: never a rate or an
 * amount. A note on a demo client is listed, marked "Demo client", and
 * counts in every figure here; the payroll file leaves it out.
 *
 * Gate: log_internal_time, the capability every roster login holds.
 */

import { type ReactNode, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Chip } from '@/components/primitives/Chip';
import { Button } from '@/components/primitives/Button';
import { PageShell } from '@/components/primitives/PageShell';
import { PaginationFooter } from '@/components/primitives/PaginationFooter';
import { InternalTimeEntries, entryDateLabel } from '@/components/time/InternalTimeCard';
import { LogInternalTimeDialog } from '@/components/time/LogInternalTimeDialog';
import { MyTimeBilling } from '@/components/time/MyTimeBilling';
import { UnpostedDraftsWarning } from '@/components/notes/UnpostedDraftsWarning';
import { PeriodHeader } from '@/components/time/PeriodHeader';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useTenant } from '@/lib/auth';
import { canLogInternalTime, canViewPrebilling } from '@/lib/capabilities';
import { addDays } from '@/lib/capacity';
import { localTodayIso } from '@/lib/cmHome';
import { formatHoursTenths } from '@/lib/hours';
import { readHoursIncrement } from '@/lib/hoursIncrement';
import { readRateBands } from '@/lib/notes';
import { daysBetween } from '@/lib/proseDate';
import { TIME_ENTRY_KIND_LABEL, useMyHours, type StaffHoursRowWire, type TimeEntryKind } from '@/lib/timeEntriesApi';

const PERIOD_PARAM = 'period';

/** Case notes per page; the whole period arrives in one read and is sliced here. */
const NOTES_PAGE_SIZE = 20;

const STAT_CELL = 'min-w-0 px-3 py-3.5 sm:px-5 sm:py-4';
const STAT_LABEL = 'text-label font-semibold uppercase tracking-label text-sage-400 break-words';
const STAT_FIGURE = 'mt-1 flex flex-wrap items-baseline gap-x-1';
const BREAKDOWN_HEADING = 'mb-1.5 text-label font-semibold uppercase tracking-label text-sage-400';
const ROW_HOURS = 'tabular-nums whitespace-nowrap text-sm text-ink';

function kindLabel(kind: string): string {
  return (TIME_ENTRY_KIND_LABEL as Record<string, string>)[kind as TimeEntryKind] ?? kind;
}

/** Newest first: work_date descending, then ref descending, whatever order the wire used. */
function newestFirst(a: StaffHoursRowWire, b: StaffHoursRowWire): number {
  if (a.work_date !== b.work_date) return a.work_date < b.work_date ? 1 : -1;
  return a.ref < b.ref ? 1 : a.ref > b.ref ? -1 : 0;
}

/** A stat's figure: the rounded hours and the unit, muted at zero. */
function hoursFigure(value: string): ReactNode {
  const shown = formatHoursTenths(value);
  const ink = shown === '0' ? 'text-ink-soft' : 'text-sage-800';
  return (
    <>
      <span className={`font-display text-2xl tabular-nums ${ink}`}>{shown}</span>{' '}
      <span className="text-sm text-ink-soft">h</span>
    </>
  );
}

export function MyTime(): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const [params, setParams] = useSearchParams();

  const allowed = canLogInternalTime(whoami);
  // A seat with view_prebilling reads its case notes as the billing view
  // (its own notes, their lines and the Ready switch); any other seat keeps
  // the ledger's list.
  const billingView = canViewPrebilling(whoami);
  const todayIso = useMemo(() => localTodayIso(), []);
  const periodParam = params.get(PERIOD_PARAM);
  const { data, isLoading, isError, error, refetch } = useMyHours(tenantSlug, periodParam, { enabled: allowed });
  const { data: tenantConfig } = useTenantConfig(tenantSlug, { enabled: allowed });
  const hoursIncrement = readHoursIncrement(tenantConfig?.config);
  const bands = useMemo(() => readRateBands(tenantConfig?.config), [tenantConfig]);
  const bandLabel = useMemo(() => {
    return (band: string | null): string => {
      if (band === null) return 'No band';
      return bands.find((b) => b.value === band)?.label ?? band;
    };
  }, [bands]);

  const [logOpen, setLogOpen] = useState(false);
  const closeLog = (): void => setLogOpen(false);

  function goTo(date: string): void {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(PERIOD_PARAM, date);
      return next;
    });
  }

  const period = data?.period ?? null;
  const summary = data?.summary ?? null;

  // Previous and Next step one period length from the URL's own date, not
  // from the period on screen: the previous period stays shown while the
  // next loads, so a second click during a load must move on again rather
  // than repeat the first. The length comes from the period on screen,
  // and the server returns the period containing whatever date it gets.
  function step(direction: -1 | 1): void {
    if (period === null) return;
    const periodDays = daysBetween(period.start, period.end) + 1;
    goTo(addDays(periodParam ?? period.start, direction * periodDays));
  }

  const noteRows = useMemo(
    () => (data?.rows ?? []).filter((r) => r.source === 'case_note').sort(newestFirst),
    [data],
  );
  // The pager's reset key: the period start plus the case-note ids, sorted
  // by id. A period change, a jump to another period, or a note added,
  // removed or replaced starts the list on page 1; internal time, a
  // changed total, an edited note, a same-content refetch and a jump that
  // lands in the same period all keep the page.
  const notesKey = useMemo(
    () => (data === null ? '' : `${data.period.start}|${noteRows.map((r) => r.ref).sort().join(',')}`),
    [data, noteRows],
  );
  const [notesPager, setNotesPager] = useState<{ key: string; offset: number }>({ key: '', offset: 0 });
  // One key is stored; a different key clears the offset during render,
  // React's pattern for state that resets when data changes, and the
  // offset is read through the same check so no frame shows a stale page.
  if (notesPager.key !== notesKey) setNotesPager({ key: notesKey, offset: 0 });
  const notesOffset = notesPager.key === notesKey ? notesPager.offset : 0;
  const pageRows = noteRows.slice(notesOffset, notesOffset + NOTES_PAGE_SIZE);

  return (
    <PageShell
      title="My time"
      // Wide: the billing view's table holds five columns, and its invoice
      // line needs room to read on one or two lines (MyTimeBilling).
      maxWidth="wide"
      access={{ allowed }}
      async={{ what: 'your hours', isLoading, isError, error, onRetry: refetch }}
      testId="my-time"
      actions={
        // The dialog mounts here, beside its button and outside the async
        // boundary: a failed background refetch swaps the page body for
        // the error banner, and a half-filled form must not go with it.
        <>
          <Button
            variant="primary"
            size="sm"
            data-testid="log-internal-time-button"
            disabled={data === null}
            onClick={() => setLogOpen(true)}
          >
            <Plus size={15} aria-hidden="true" />
            Log internal time
          </Button>
          {logOpen && data !== null && (
            <LogInternalTimeDialog
              tenantSlug={tenantSlug}
              period={data.period}
              configPresent={data.config_present}
              hoursIncrement={hoursIncrement}
              todayIso={todayIso}
              onClose={closeLog}
            />
          )}
        </>
      }
    >
      {period !== null && summary !== null && data !== null && (
        <div className="flex flex-col gap-6 sm:gap-7">
          <PeriodHeader
            period={period}
            todayIso={todayIso}
            onPrevious={() => step(-1)}
            onNext={() => step(1)}
            onGoTo={goTo}
          />

          <section
            aria-label="This period"
            data-testid="my-time-summary"
            className="rounded-card border border-sage-200 bg-paper shadow-sh1"
          >
            <div className="grid grid-cols-3" data-testid="summary-stats">
              {/* Total leads: its cell sits on the sage tint, set apart from
                  the two figures it adds up. */}
              <div className={`${STAT_CELL} rounded-tl-card bg-sage-50`} data-testid="summary-stat">
                <p className={STAT_LABEL}>Total</p>
                <p className={STAT_FIGURE} data-testid="summary-total">
                  {/* Gated on what counts, demo notes included, never on the rounded figure: a period whose only counted row is 0.04 h reads "0 h". */}
                  {summary.clients.length === 0 && summary.internal.length === 0 ? (
                    <span className="font-display italic text-base text-ink-soft sm:text-lg">No hours yet</span>
                  ) : (
                    hoursFigure(summary.total_hours)
                  )}
                </p>
              </div>
              <div className={`${STAT_CELL} border-l border-sage-100`} data-testid="summary-stat">
                <p className={STAT_LABEL}>Client work</p>
                <p className={STAT_FIGURE} data-testid="summary-client-hours">
                  {hoursFigure(summary.client_hours)}
                </p>
                {formatHoursTenths(summary.after_hours) !== '0' && (
                  <p className="mt-0.5 text-xs text-ink-soft" data-testid="summary-after-hours">
                    Includes {formatHoursTenths(summary.after_hours)} h After Hours
                  </p>
                )}
              </div>
              <div className={`${STAT_CELL} border-l border-sage-100`} data-testid="summary-stat">
                <p className={STAT_LABEL}>Internal</p>
                <p className={STAT_FIGURE} data-testid="summary-internal-hours">
                  {hoursFigure(summary.internal_hours)}
                </p>
              </div>
            </div>

            <div
              className="grid gap-4.5 border-t border-sage-100 px-4 pt-3.5 pb-2 sm:grid-cols-2 sm:gap-10 sm:px-5 sm:pt-4 sm:pb-2.5"
              data-testid="summary-breakdown"
            >
              <div data-testid="summary-by-client">
                <h3 className={BREAKDOWN_HEADING}>By client</h3>
                {summary.clients.length === 0 ? (
                  <p className="text-sm text-ink-soft" data-testid="summary-clients-empty">Once you post a case note with hours, you'll see the client work here.</p>
                ) : (
                  <ul className="m-0 list-none divide-y divide-sage-100 p-0">
                    {summary.clients.map((c) => (
                      <li key={c.client_id} className="py-2 first:pt-0.5" data-testid="summary-client">
                        <div className="flex items-baseline justify-between gap-3">
                          <Link to={`/t/${tenantSlug}/clients/${c.client_id}`} className="text-value font-medium text-sage-800 hover:underline">
                            {c.client_display_name}
                          </Link>
                          <span className={ROW_HOURS}>{formatHoursTenths(c.hours)} h</span>
                        </div>
                        <dl data-testid="summary-client-bands" className="mt-0.5 flex flex-col gap-0.5">
                          {c.bands.map((b) => (
                            <div key={b.rate_band ?? ''} className="flex flex-wrap items-baseline gap-x-2">
                              <dt className="text-label font-semibold uppercase tracking-label text-sage-400">{bandLabel(b.rate_band)}</dt>
                              <dd className="text-value font-medium text-ink">{formatHoursTenths(b.hours)} h</dd>
                            </div>
                          ))}
                        </dl>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div data-testid="summary-internal">
                <h3 className={BREAKDOWN_HEADING}>Internal</h3>
                {summary.internal.length === 0 ? (
                  <p className="text-sm text-ink-soft" data-testid="summary-internal-empty">Log admin, training or meeting time and you'll see it here.</p>
                ) : (
                  <>
                    <ul className="m-0 list-none divide-y divide-sage-100 p-0">
                      {summary.internal.map((k) => (
                        <li key={k.kind} className="flex items-baseline justify-between gap-3 py-2 first:pt-0.5" data-testid="summary-internal-kind">
                          <span className="text-value font-medium text-ink">{kindLabel(k.kind)}</span>
                          <span className={ROW_HOURS}>{formatHoursTenths(k.hours)} h</span>
                        </li>
                      ))}
                    </ul>
                    <InternalTimeEntries
                      tenantSlug={tenantSlug}
                      period={period}
                      hoursIncrement={hoursIncrement}
                      todayIso={todayIso}
                    />
                  </>
                )}
              </div>
            </div>
          </section>

          <section aria-labelledby="my-time-notes-heading" data-testid="my-time-notes">
            <h2 id="my-time-notes-heading" className="mb-3 font-display text-xl text-sage-800">
              Case notes this period
            </h2>
            <UnpostedDraftsWarning className="mb-3" />
            {billingView ? (
              <MyTimeBilling
                tenantSlug={tenantSlug}
                periodStart={periodParam}
                bands={bands}
                fixNoteId={params.get('fix_note')}
              />
            ) : noteRows.length === 0 ? (
              <p className="text-sm text-ink-soft" data-testid="case-notes-empty">
                Post a case note with hours and you'll see it here.
              </p>
            ) : (
              <>
                <div className="rounded-card border border-sage-200 bg-paper px-4 py-1 shadow-sh1 sm:px-5">
                  <ul className="m-0 list-none divide-y divide-sage-100 p-0">
                    {pageRows.map((r) => (
                      <li
                        key={r.ref}
                        className="grid grid-cols-[4.75rem_minmax(0,1fr)_auto] items-baseline gap-x-2.5 gap-y-1.5 py-2.5 sm:grid-cols-[5.75rem_minmax(0,1fr)_auto] sm:gap-x-3.5"
                        data-testid="case-note-line"
                      >
                        <span className="whitespace-nowrap text-sm tabular-nums text-ink-soft" data-testid="case-note-date">
                          {entryDateLabel(r.work_date)}
                        </span>
                        <div className="min-w-0">
                          {/* A client's name may hold a run with no break
                              in it; it wraps inside its column rather than
                              pushing the page sideways. */}
                          <p className="text-value font-medium leading-5 break-words">
                            <Link
                              to={`/t/${tenantSlug}/clients/${r.client_id}?tab=notes#note-${r.ref}`}
                              className="text-sage-800 hover:underline"
                              data-testid="case-note-link"
                            >
                              {r.client_display_name}
                            </Link>
                          </p>
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-soft">
                            <span>{bandLabel(r.rate_band)}</span>
                            {r.after_hours && <Chip variant="caution">After Hours</Chip>}
                            {r.kind === 'assessment' && <Chip variant="neutral">Assessment</Chip>}
                            {r.client_is_demo && (
                              <Chip variant="neutral" data-testid="case-note-demo">
                                Demo client
                              </Chip>
                            )}
                          </p>
                        </div>
                        <span className="whitespace-nowrap text-sm tabular-nums text-ink" data-testid="case-note-hours">
                          {formatHoursTenths(r.hours)} h
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <PaginationFooter
                  offset={notesOffset}
                  pageSize={NOTES_PAGE_SIZE}
                  total={noteRows.length}
                  onOffsetChange={(offset) => setNotesPager({ key: notesKey, offset })}
                  testIdPrefix="case-notes-page-"
                />
              </>
            )}
          </section>
        </div>
      )}
    </PageShell>
  );
}
